import React, { useContext, useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import {
  TextField,
  Button,
  Typography,
  FormControl,
  FormHelperText,
  Grid,
  Container,
  InputLabel,
  MenuItem,
  Select,
  Box,
  CircularProgress,
} from '@mui/material';
import { investorClassifications, investorTypes } from '../../utils/constants';
import { CustomColors, theme } from '../../theme';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { QueryKeys } from '../../react-query/QueryKeys';
import { editUser, getUser } from '../../mutations/mutations';
import { AuthContext } from '../../auth/AuthContext';
import { toast } from 'react-toastify';
import Alert from '@mui/material/Alert';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  investorType: string;
  // investorClassification?: string;
  entityName: string;
  location: string;
  entityWebAddress: string;
  linkedInProfile?: string;
}

const Profile = () => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>();

  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [showOtherInvestorType, setShowOtherInvestorType] = useState(false);
  const [otherInvestorTypeError, setOtherInvestorTypeError] = useState(false);
  const [otherInvestorType, setOtherInvestorType] = useState('');

  const investorType = watch('investorType');

  useEffect(() => {
    if (investorType === 'Other Please state') {
      setShowOtherInvestorType(true);
    } else {
      setOtherInvestorType('');
      setShowOtherInvestorType(false);
    }
  }, [investorType]);

  const { user } = useContext(AuthContext);

  const editUserMutation = useMutation(editUser, {
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      setLoading(false);
      toast.success('User Edited successfully');
    },
    onError: (error) => {
      setLoading(false);
      console.error('Error editing user:', error);
      toast.error('Error editing user');
    },
  });

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      setOtherInvestorTypeError(false);
      if (investorType === 'Other Please state') {
        if (otherInvestorType === '') {
          setOtherInvestorTypeError(true);
          return;
        }
        data.investorType = otherInvestorType;
      }

      if (user?.sub) {
        await editUserMutation.mutateAsync({
          id: user?.sub,
          form: data,
        });
        await queryClient.invalidateQueries([QueryKeys.PROFILE]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const { data: userData, isLoading } = useQuery(QueryKeys.PROFILE, () =>
    getUser(user.sub)
  );

  useEffect(() => {
    if (userData) {
    }

    reset({
      firstName: userData?.user_metadata?.firstName,
      lastName: userData?.user_metadata?.lastName,
      email: userData?.email,
      phone: userData?.user_metadata?.phone,
      investorType: userData?.user_metadata?.investorType,
      // investorClassification: userData?.user_metadata?.investorClassification,
      entityName: userData?.user_metadata?.entityName,
      location: userData?.user_metadata?.location,
      entityWebAddress: userData?.user_metadata?.entityWebAddress,
      linkedInProfile: userData?.user_metadata?.linkedInProfile,
    });
  }, [reset, userData]);

  const handleOtherInvestorType = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setOtherInvestorType(e.target.value);
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h2">PROFILE SETTINGS</Typography>
      <Typography variant="h6" marginY={2}>
        Your profiles
      </Typography>

      {isLoading && (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}
        >
          <CircularProgress />
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="firstName"
                control={control}
                disabled
                defaultValue=""
                rules={{
                  required: 'First Name is required',
                  minLength: {
                    value: 4,
                    message: 'First Name must be at least 4 characters',
                  },
                }}
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      label="First Name"
                      variant="outlined"
                      error={!!errors.firstName}
                    />
                    {errors.firstName && (
                      <FormHelperText error>
                        {errors.firstName.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <Controller
                name="lastName"
                control={control}
                disabled
                defaultValue=""
                rules={{
                  required: 'Last Name is required',
                  minLength: {
                    value: 4,
                    message: 'Last Name must be at least 4 characters',
                  },
                }}
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      label="Last Name"
                      variant="outlined"
                      error={!!errors.lastName}
                    />
                    {errors.lastName && (
                      <FormHelperText error>
                        {errors.lastName.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FormControl>
          </Grid>
        </Grid>

        <FormControl fullWidth margin="normal">
          <Controller
            name="email"
            control={control}
            disabled
            defaultValue=""
            rules={{
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Email"
                  variant="outlined"
                  error={!!errors.email}
                />
                {errors.email && (
                  <FormHelperText error>{errors.email.message}</FormHelperText>
                )}
              </>
            )}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <Controller
            name="phone"
            control={control}
            disabled
            defaultValue=""
            rules={{
              required: 'Phone is required',
              pattern: {
                value:
                  /^(\+?\d{1,3}[-.\s]?)?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
                message: 'Invalid phone number',
              },
            }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Phone"
                  variant="outlined"
                  error={!!errors.phone}
                />
                {errors.phone && (
                  <FormHelperText error>{errors.phone.message}</FormHelperText>
                )}
              </>
            )}
          />
        </FormControl>

        <Alert
          sx={{
            backgroundColor: '#e5f6fd',
            // change icon color
            '& .MuiAlert-icon': { color: '#2196f3' },
            marginBottom: 2,
          }}
          severity="info"
        >
          If you would like to change your personal information, please get in
          touch by clicking on the contact button on the top left.
        </Alert>

        <FormControl fullWidth margin="normal">
          <Controller
            name="linkedInProfile"
            control={control}
            defaultValue=""
            rules={{
              required: 'LinkedIn Profile is required',
              minLength: {
                value: 4,
                message: 'LinkedIn Profile must be at least 4 characters',
              },
            }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="LinkedIn Profile"
                  variant="outlined"
                  error={!!errors.linkedInProfile}
                />
                {errors.linkedInProfile && (
                  <FormHelperText error>
                    {errors.linkedInProfile.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Investor Type</InputLabel>
          <Controller
            name="investorType"
            control={control}
            defaultValue=""
            rules={{ required: 'Investor Type is required' }}
            render={({ field }) => (
              <>
                <Select {...field} variant="outlined" label="Investor Type">
                  {investorTypes.map((type: string) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {errors.investorType && (
                  <FormHelperText error>
                    {errors.investorType.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </FormControl>
        {/* 
        {showOtherInvestorType && (
          <FormControl fullWidth margin="normal">
            <TextField
              label="Other Investor Type"
              onChange={handleOtherInvestorType}
              value={otherInvestorType}
              variant="outlined"
            />
            {otherInvestorTypeError && (
              <FormHelperText error>
                {"Please enter the investor's type"}
              </FormHelperText>
            )}
          </FormControl>
        )} */}

        {/* <FormControl fullWidth margin="normal">
          <InputLabel>Investor Classification</InputLabel>
          <Controller
            name="investorClassification"
            control={control}
            defaultValue=""
            rules={{ required: 'Investor Classification is required' }}
            render={({ field }) => (
              <>
                <Select
                  {...field}
                  variant="outlined"
                  label="Investor Classification"
                >
                  {investorClassifications.map((type: string) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {errors.investorClassification && (
                  <FormHelperText error>
                    {errors.investorClassification.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </FormControl> */}

        <Typography variant="h6" marginY={2}>
          Company Detail
        </Typography>

        <FormControl fullWidth margin="normal">
          <Controller
            name="entityName"
            control={control}
            defaultValue=""
            rules={{
              required: 'Entity Name is required',
              minLength: {
                value: 4,
                message: 'Entity must be at least 4 characters',
              },
            }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Entity Name"
                  variant="outlined"
                  error={!!errors.entityName}
                />
                {errors.entityName && (
                  <FormHelperText error>
                    {errors.entityName.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <Controller
            name="location"
            control={control}
            defaultValue=""
            rules={{
              required: 'Location is required',
              minLength: {
                value: 4,
                message: 'Location must be at least 4 characters',
              },
            }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Location"
                  variant="outlined"
                  error={!!errors.location}
                />
                {errors.location && (
                  <FormHelperText error>
                    {errors.location.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </FormControl>

        <FormControl fullWidth margin="normal">
          <Controller
            name="entityWebAddress"
            control={control}
            defaultValue=""
            rules={{
              required: 'Entity Web Address is required',
              minLength: {
                value: 4,
                message: 'Entity Web Address must be at least 4 characters',
              },
            }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Entity Web Address"
                  variant="outlined"
                  error={!!errors.entityWebAddress}
                />
                {errors.entityWebAddress && (
                  <FormHelperText error>
                    {errors.entityWebAddress.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </FormControl>

        <Button
          style={styles.button}
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          size="large"
        >
          Save Changes
        </Button>
      </form>
    </Container>
  );
};

export default Profile;

const styles: { [key: string]: React.CSSProperties } = {
  h1: {
    fontSize: theme.spacing(5),
    lineHeight: theme.spacing(5),
    color: CustomColors.foregroundColor,
  },
  p: {
    color: CustomColors.grey,
  },
  container: {
    marginTop: theme.spacing(8),
    padding: theme.spacing(5),
  },
  signUpContainer: {
    marginTop: theme.spacing(8),
  },
  logo: {
    width: theme.spacing(20),
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
  },
  button: {
    marginTop: theme.spacing(5),
  },
};
